import React, { MutableRefObject, ReactElement, useCallback, useContext, useEffect } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { DarkMode } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { IconName } from '../Components/Icon'
import IconButton, { IconButtonType } from '../Components/IconButton'
import { MessageContext } from '../Contexts'
import { AvatarMenuItem } from '../Types'
import { ReactState } from '../Types/Types'
import { UserSettings, useUserSettings } from './useUserSettings'
import { MessageContextType } from '../Contexts/MessageContext'
import { UserInfo, useUserInfo } from '../../../ReactRouting/Hooks/useUserInfo'
import { useSessionState } from './useSessionState'
import { usePersistentState } from './usePersistState'
import { userSettings$ } from '../../../NonReact/common/RxJS/UserSettingsState'
import { updateUserDarkmode } from '../../../NonReact/common/API/EntityAPIClient'

export const DARK_MODE_CLASS: string = 'dark-mode'
export const DARK_MODE_STORAGE: string = 'isDarkMode'

export type DarkModeType = {
    isDarkMode: boolean
    darkModeSwitch: ReactElement
    darkModeAvatarMenu: AvatarMenuItem
}

export const useDarkMode: () => DarkModeType = (): DarkModeType => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(DarkMode.NAME)

    const [userInfo]: UserInfo = useUserInfo(true)
    const [userSettings]: UserSettings = useUserSettings()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const { displayError }: MessageContextType = useContext(MessageContext)

    const [isDarkMode, setIsDarkMode]: ReactState<Nullable<boolean>> = usePersistentState<Nullable<boolean>>(
        DARK_MODE_STORAGE,
        false
    )
    const [darkModeSetOutsideLogin, setDarkModeSetOutsideLogin]: ReactState<Nullable<boolean>> = useSessionState<
        Nullable<boolean>
    >(DARK_MODE_STORAGE, undefined)

    const handleDarkModeChange: () => void = useCallback((): void => {
        const newDarkMode: boolean = !isDarkMode
        setIsDarkMode(newDarkMode)

        if (isVarNotEmpty(userInfo)) {
            updateUserDarkmode(newDarkMode)
                .then((): void => {
                    userSettings$?.resolver?.()
                })
                .catch(displayError)
        } else {
            setDarkModeSetOutsideLogin(newDarkMode)
        }
    }, [isDarkMode, userInfo])

    useEffect((): void => {
        if (isMounted.current) {
            if (isVarNotEmpty(darkModeSetOutsideLogin) && isVarNotEmpty(userSettings)) {
                updateUserDarkmode(darkModeSetOutsideLogin)
                    .then((): void => {
                        userSettings$?.resolver?.()
                    })
                    .catch(displayError)
                setDarkModeSetOutsideLogin(undefined)
            } else if (isVarNotEmpty(userSettings)) {
                setIsDarkMode(userSettings.DarkMode)
            }
        }
    }, [userSettings, darkModeSetOutsideLogin])

    useEffect((): void => {
        if (isDarkMode) {
            document.body.classList.add(DARK_MODE_CLASS)
        } else {
            document.body.classList.remove(DARK_MODE_CLASS)
        }
    }, [isDarkMode])

    return {
        isDarkMode: !!isDarkMode,
        darkModeSwitch: (
            <IconButton
                customClassName='dark-mode-switch'
                iconName={isDarkMode ? IconName.DARK_MODE : IconName.LIGHT_MODE}
                type={IconButtonType.BUTTON}
                onClick={(): void => handleDarkModeChange()}
            />
        ),
        darkModeAvatarMenu: {
            icon: isDarkMode ? IconName.LIGHT_MODE : IconName.DARK_MODE,
            label: language.get(isDarkMode ? DarkMode.DarkMode_SwitchLight_Title : DarkMode.DarkMode_Switch_Title),
            action: (): void => handleDarkModeChange()
        }
    }
}
